export interface LabCheckoutAttempt {
  request_id: string;
  checkout_token: string;
  product_id: string;
  owner_id: string;
}

const prefix = "system.labs.checkout.v1:";
const handoffPrefix = "system.labs.claim.v1:";
let capturedHandoff: { checkout_id: string; checkout_token: string } | null =
  null;

export function readLabCheckoutAttempt(
  ownerId: string,
  productId: string,
): LabCheckoutAttempt | null {
  try {
    const value = JSON.parse(
      sessionStorage.getItem(`${prefix}${ownerId}:${productId}`) ?? "null",
    ) as LabCheckoutAttempt | null;
    return value?.owner_id === ownerId &&
      value.product_id === productId &&
      value.request_id &&
      value.checkout_token
      ? value
      : null;
  } catch {
    return null;
  }
}

export function createLabCheckoutAttempt(
  ownerId: string,
  productId: string,
): LabCheckoutAttempt {
  const attempt = {
    request_id: crypto.randomUUID(),
    checkout_token: crypto.randomUUID() + crypto.randomUUID(),
    product_id: productId,
    owner_id: ownerId,
  };
  // Persist the recovery identity before any request. A failed write must not
  // start payment, because a reload could otherwise lose its retry identity.
  sessionStorage.setItem(
    `${prefix}${ownerId}:${productId}`,
    JSON.stringify(attempt),
  );
  return attempt;
}

export function clearLabCheckoutAttempt(ownerId: string, productId: string) {
  try {
    sessionStorage.removeItem(`${prefix}${ownerId}:${productId}`);
  } catch {
    /* optional cleanup after verified completion */
  }
}

/** Runs before analytics initialization; the handoff secret never stays in a captured URL. */
export function captureLabCheckoutHandoff() {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/app/labs/")) {
    const params = new URLSearchParams(window.location.search);
    if (params.has("payment_intent_client_secret")) {
      params.delete("payment_intent_client_secret");
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${params.size ? `?${params}` : ""}${window.location.hash}`,
      );
    }
  }
  if (window.location.pathname !== "/labs/claim") return;
  const checkoutId = new URLSearchParams(window.location.search).get(
    "checkout_id",
  );
  const token = new URLSearchParams(window.location.hash.slice(1)).get(
    "checkout_token",
  );
  if (!checkoutId || !token) return;
  capturedHandoff = { checkout_id: checkoutId, checkout_token: token };
  try {
    sessionStorage.setItem(`${handoffPrefix}${checkoutId}`, token);
  } catch {
    /* current page can still claim from memory */
  }
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}`,
  );
}

export function readLabCheckoutHandoff(checkoutId: string): string | null {
  if (capturedHandoff?.checkout_id === checkoutId)
    return capturedHandoff.checkout_token;
  try {
    return sessionStorage.getItem(`${handoffPrefix}${checkoutId}`);
  } catch {
    return null;
  }
}

export function clearLabCheckoutHandoff(checkoutId: string) {
  if (capturedHandoff?.checkout_id === checkoutId) capturedHandoff = null;
  try {
    sessionStorage.removeItem(`${handoffPrefix}${checkoutId}`);
  } catch {
    /* no additional network action */
  }
}
